import type { ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  hasInvolvementEnded,
  type AssessmentId,
  type GuestOnCase,
  type GuestOnCaseDetails,
} from "@thalamos/haven-common";

const calendarDate = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

/** Where reading a guest's Details has got. */
export type GuestDetailsReading =
  | { kind: "reading" }
  | { kind: "failed" }
  | { kind: "read"; details: GuestOnCaseDetails };

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="dt">
      {label}
    </Typography>
    <Typography variant="body2" component="dd" sx={{ m: 0 }}>
      {children}
    </Typography>
  </Box>
);

const DetailsBody = ({
  details,
  assessmentHref,
}: {
  details: GuestOnCaseDetails;
  assessmentHref: (assessmentId: AssessmentId) => string;
}) => {
  const { t } = useTranslation();
  const { use } = details;
  const addedBy = t("guestDetails.byOn", {
    name: details.startedByUserName,
    date: calendarDate.format(details.startedAt),
  });

  return (
    <Stack spacing={2} component="dl" sx={{ m: 0 }}>
      <Field label={t("guestDetails.email")}>{details.email}</Field>
      <Field
        label={
          use.kind === "read_shared_assessments"
            ? t("guestDetails.sharedBy")
            : t("guestDetails.askedBy")
        }
      >
        {addedBy}
      </Field>
      {details.endedAt !== null && hasInvolvementEnded(details) ? (
        <Field label={t("guestDetails.endedOn")}>
          {calendarDate.format(details.endedAt)}
        </Field>
      ) : details.expiresAt === null ? null : (
        <Field
          label={
            use.kind === "read_shared_assessments"
              ? t("guestDetails.until")
              : t("guestDetails.sendBy")
          }
        >
          {calendarDate.format(details.expiresAt)}
        </Field>
      )}
      <Field label={t("guestDetails.hasAccessed")}>
        {details.signIns === null
          ? t("guestDetails.notYet")
          : t("guestDetails.accessed", {
              first: calendarDate.format(details.signIns.first),
              last: calendarDate.format(details.signIns.last),
            })}
      </Field>

      {match(use)
        .with({ kind: "read_shared_assessments" }, ({ assessments }) => (
          <Field
            label={t("guestDetails.shared", { count: assessments.length })}
          >
            {assessments.length === 0 ? (
              t("guestDetails.noneShared")
            ) : (
              <List
                dense
                disablePadding
                sx={{ maxHeight: 240, overflowY: "auto" }}
              >
                {assessments.map((assessment) => (
                  <ListItem
                    key={assessment.assessmentId}
                    disableGutters
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    <Link
                      component={RouterLink}
                      to={assessmentHref(assessment.assessmentId)}
                    >
                      {calendarDate.format(assessment.finishedAt)}
                    </Link>
                    <Typography variant="body2" component="span">
                      {t(`guestDetails.status.${assessment.status}`)}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="span"
                      color="text.secondary"
                    >
                      {assessment.firstOpenedAt === null
                        ? t("guestDetails.notOpened")
                        : t("guestDetails.opened", {
                            date: calendarDate.format(assessment.firstOpenedAt),
                          })}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Field>
        ))
        .with(
          { kind: "complete_assessment" },
          ({ assessmentId, progress, message, sentAt }) => (
            <>
              <Field label={t("guestDetails.progress")}>
                {progress === null ? (
                  t("guestDetails.progressGone")
                ) : (
                  <Stack spacing={0.5} component="span">
                    <LinearProgress
                      variant="determinate"
                      aria-label={t("guestDetails.progress")}
                      value={
                        progress.included === 0
                          ? 0
                          : (100 * progress.completed) / progress.included
                      }
                    />
                    <span>
                      {t("guestDetails.domainsAnswered", {
                        completed: progress.completed,
                        included: progress.included,
                      })}
                      {" · "}
                      {sentAt === null
                        ? t("guestDetails.notSent")
                        : t("guestDetails.sent", {
                            date: calendarDate.format(sentAt),
                          })}
                    </span>
                  </Stack>
                )}
              </Field>
              {sentAt === null ? null : (
                <Link component={RouterLink} to={assessmentHref(assessmentId)}>
                  {t("guestDetails.openAssessment")}
                </Link>
              )}
              <Field label={t("guestDetails.message")}>
                {message ?? t("guestDetails.noMessage")}
              </Field>
            </>
          ),
        )
        .exhaustive()}
    </Stack>
  );
};

/**
 * One guest's Details: who they are, the address their link went to, who
 * added them, whether they have signed in, and what they have done with what
 * they were given. Offers End access where `onEnd` is given and the guest's
 * access has not ended.
 */
export const GuestDetailsDialog = ({
  guest,
  reading,
  assessmentHref,
  onEnd,
  onClose,
}: {
  /** The guest whose Details are open, or `null` while the dialog is closed. */
  guest: GuestOnCase | null;
  reading: GuestDetailsReading;
  assessmentHref: (assessmentId: AssessmentId) => string;
  onEnd?: (() => Promisable<void>) | undefined;
  onClose: () => Promisable<void>;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={guest !== null}
      onClose={() => {
        void onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      {guest === null ? null : (
        <>
          <DialogTitle>
            {guest.name}
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              display="block"
            >
              {[guest.jobTitle, t(`caseGuests.role.${guest.access.kind}`)]
                .filter((part) => part !== null)
                .join(" · ")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {match(reading)
              .with({ kind: "reading" }, () => (
                <Typography variant="body2" color="text.secondary">
                  {t("guestDetails.loading")}
                </Typography>
              ))
              .with({ kind: "failed" }, () => (
                <Alert severity="error">{t("guestDetails.failed")}</Alert>
              ))
              .with({ kind: "read" }, ({ details }) => (
                <DetailsBody
                  details={details}
                  assessmentHref={assessmentHref}
                />
              ))
              .exhaustive()}
          </DialogContent>
          <DialogActions>
            {onEnd === undefined || hasInvolvementEnded(guest) ? null : (
              <Button
                color="error"
                onClick={() => {
                  void onEnd();
                }}
              >
                {t("endGuestAccess.open")}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => {
                void onClose();
              }}
            >
              {t("guestDetails.close")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
